import type { ChannelAuthorizationCallback } from 'pusher-js'
import Pusher from 'pusher-js'
import * as Sentry from '@sentry/browser'
import { UsersApi } from './apiClient'
import { getAxiosAuthorizationHeader } from './apiHelpers'

async function getPusherAuthToken(
  usersAPI: UsersApi,
  channelName: string,
  socketId: string
): Promise<{ auth: string }> {
  const resp = await usersAPI.getTokenForPusherUsersTokenForPusherPost(
    channelName,
    socketId
  )
  return { auth: resp.data.auth }
}

async function getPusherAuthTokenWithRetry(
  channelName: string,
  socketId: string,
  callback: ChannelAuthorizationCallback
): Promise<void> {
  let retryCount = 0
  const maxRetries = 5
  const retryDelay = 1000 // 1 second
  const usersAPI = new UsersApi()

  const attemptAuth = async () => {
    try {
      const auth = await getPusherAuthToken(usersAPI, channelName, socketId)
      callback(null, auth)
    } catch (err: any) {
      retryCount++
      if (
        err.response?.status >= 500 &&
        err.response?.status < 600 &&
        retryCount < maxRetries
      ) {
        console.warn(
          `Received ${err.response.status} when trying to connect to channel ${channelName}, retry ${retryCount}/${maxRetries}...`
        )
        setTimeout(attemptAuth, retryDelay * 2 ** (retryCount - 1))
      } else {
        const errorMessage = `Failed to connect to channel ${channelName} after ${retryCount} retries: ${err}`
        console.error(errorMessage, err)
        callback(new Error(errorMessage), null)
      }
    }
  }

  attemptAuth()
}

export function connectToSoketi(): Pusher {
  // https://www.npmjs.com/package/pusher-js
  const client = new Pusher('draydog', {
    wsHost: 'ws.draydog.com',
    wsPort: 443,
    wssPort: 443,
    cluster: 'not_used', // Not used but typing requires it
    forceTLS: true,
    // This needs to be both ws and wss of the connection will have state "failed." Per
    // the Pusher docs, they way to force TLS is to set forceTLS to true but still have
    // ws and wss in the enabledTransports array.
    enabledTransports: ['ws', 'wss'],
    disableStats: true, // These would be sent to pusher.com
    // authEndpoint: 'https://api.draydog.com/pusher/auth',
    channelAuthorization: {
      // https://pusher.com/docs/channels/using_channels/connection/#userauthcustomhandler-1319001424
      customHandler: (
        info: { channelName: string; socketId: string },
        callback: ChannelAuthorizationCallback
      ) => {
        const authHeader = getAxiosAuthorizationHeader()
        if (!authHeader) {
          console.error(
            `No auth header set when trying to connect to channel ${info.channelName}`
          )
          callback(new Error(`No auth header set`), null)
          return
        }
        // I was going to throw some caching in here, but it runs out that auth tokens
        // are tied to socketIDs so it doesn't really work to cache them
        getPusherAuthTokenWithRetry(info.channelName, info.socketId, callback)
      },
    },
  })

  // https://pusher.com/docs/channels/using_channels/connection/#available-states
  if (client.connection.state === 'failed') {
    Sentry.captureMessage('Pusher connection failed, possibly misconfigured')
  }
  // client.connection.bind_global((eventName, data) => {
  //   console.log('global Soketi connection event:', eventName, data)
  // })
  return client
}
